// Lightweight adapters to normalize v4 payloads to legacy (v3-like) shapes.
// These run in selectors so components can keep consuming legacy shapes.
import { pacingNodesData } from 'components/PacingModal/data'
import { resolveIsExpired } from 'utils/licenseHelpers'

const createWarnCollector = funcName => {
  const counts = new Map()
  const add = field => counts.set(field, (counts.get(field) || 0) + 1)
  const flush = () => {
    if (!counts.size) return
    const summary = Array.from(counts.entries())
      .map(([f, c]) => `${f} (count=${c})`)
      .join(', ')
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(`[adapters] ${funcName}: omitted fields -> ${summary}`)
    }
  }
  return { add, flush }
}

// Best-effort mapping for legacy skuCode/type from v4 product
// - If both K5P and MSP present, treat as K8P (multi-program)
// - If description includes "Family", append F (e.g., K8PF)
// - Else prefer first programCode as type
const deriveSkuCode = product => {
  if (!product) return undefined
  const { programCodes = [], description } = product
  const hasK5P = programCodes.includes('K5P')
  const hasMSP = programCodes.includes('MSP')
  const isFamily =
    typeof description === 'string' &&
    description.toLowerCase().includes('family')

  let base
  if (hasK5P && hasMSP) base = 'K8P'
  else if (hasK5P) base = 'K5P'
  else if (hasMSP) base = 'MSP'
  else base = programCodes[0]

  if (!base) return undefined
  return isFamily ? `${base}F` : base
}

// Map v4 program identifiers to legacy sitePreferences keys
const mapProgramToPreferencesKey = ({ programCode, accessCode }) => {
  if (programCode === 'K5P' || accessCode === 'k5SelDigital') return 'k5Digital'
  if (programCode === 'MSP' || accessCode === 'mspDigital') return 'mspDigital'
  if (programCode === 'HSP' || accessCode === 'highSchool') return 'highSchool'
  return null
}

// Convert preference name like "BullyingHarassmentUnitEnabled" to camelCase
const toCamelCaseName = name => {
  if (typeof name !== 'string' || !name.length) return name
  return name.charAt(0).toLowerCase() + name.slice(1)
}

// Convert v4 preferences[] array to legacy sitePreferences object
const convertPreferencesArrayToLegacySitePreferences = preferencesArray => {
  const sitePreferences = {}
  if (!Array.isArray(preferencesArray)) return sitePreferences

  for (const pref of preferencesArray) {
    if (!pref) continue
    const programKey = mapProgramToPreferencesKey({
      programCode: pref.programCode,
      accessCode: pref.accessCode,
    })
    if (!programKey) continue

    if (!sitePreferences[programKey]) sitePreferences[programKey] = {}

    const target =
      pref.assignmentType === 'SiteUser'
        ? (sitePreferences[programKey].userProgramPreferences =
            sitePreferences[programKey].userProgramPreferences || {})
        : sitePreferences[programKey]

    const key = toCamelCaseName(pref.name)

    // Handle value conversion - ensure booleans are properly coerced
    // Use the value as-is if it's already a boolean, otherwise normalize it
    let value = pref.value
    if (pref.valueType === 'Boolean') {
      if (typeof pref.value === 'boolean') {
        value = pref.value
      } else if (pref.value === null || pref.value === undefined) {
        // For boolean preferences, null/undefined should remain null
        // (they might not have been set yet, unlike false which is an explicit value)
        value = null
      } else {
        // Coerce string/number to boolean
        value = pref.value === 'true' || pref.value === true || pref.value === 1
      }
    }

    // For K5P and MSP, if the preference is not completed, set the value to null
    // primarily for publish flows migration
    // TODO: Remove this and Make a proper solution for detecting Draft and Completed status
    if (
      (pref.programCode === 'K5P' || pref.programCode === 'MSP') &&
      (!pref.name === 'ProgramTrack' ||
        !pref.name === 'TeachesLessons' ||
        !pref.name === 'YearsTeaching' ||
        pref.name === 'ClassCreationGoalValue' ||
        pref.name === 'LessonCompletionGoalDate' ||
        pref.name === 'LessonCompletionGoalValue' ||
        pref.name === 'Goal3Date' ||
        pref.name === 'Goal3Text' ||
        pref.name === 'Goal4Date' ||
        pref.name === 'Goal4Text' ||
        pref.name === 'Goal5Date' ||
        pref.name === 'Goal5Text')
    ) {
      if (!pref.isCompleted) {
        value = null
      }
    }

    target[key] = value

    // Handle isCompleted flag: if a preference has isCompleted set to true,
    // also set the corresponding *IsCompleted field in legacy format
    // Use truthy check instead of strict === true to handle various truthy values
    const isCompletedKey = `${key}IsCompleted`
    if (pref.isCompleted) {
      target[isCompletedKey] = true
    }
  }

  return sitePreferences
}

export const adaptNetSuiteAssetToLegacyLicense = (
  asset,
  siteContext,
  warnCollector,
  derivedProgramCodes,
) => {
  if (!asset) return null
  const rawProduct = asset.product || {}
  // Replace product.programCodes with derivedProgramCodes from site capabilities if provided
  const effectiveProgramCodes =
    Array.isArray(derivedProgramCodes) && derivedProgramCodes.length
      ? derivedProgramCodes
      : rawProduct.programCodes || []
  // Shallow clone product to avoid mutating upstream asset
  const product = { ...rawProduct, programCodes: effectiveProgramCodes }
  const skuCode = deriveSkuCode(product)

  const wc =
    warnCollector || createWarnCollector('adaptNetSuiteAssetToLegacyLicense')

  const license = {
    id: asset.id,
    // Dates
    startDate: asset.startDate || null,
    expirationDate: asset.expirationDate || null,
    // Status flags
    isExpired: resolveIsExpired({
      isActive: asset.isActive,
      status: asset.status,
      expirationDate: asset.expirationDate,
    }),
    // Product mapping
    product: {
      id: product.id,
      skuCode, // legacy field used by selectors
      description: product.description,
      // Legacy code often expects a pricing category on product.type.
      // V4 does not expose this directly; default to 'BULK' which is the
      // category used for schoolwide digital licenses that drive dashboard
      // expired-license notifications.
      // If a more specific mapping becomes available, we can refine this.
      type: 'BULK',
      programCodes: product.programCodes,
      // legacy nested sku object
      sku: product.skuValue ? { skuValue: product.skuValue } : undefined,
    },
    // Site context (some consumers expect these alongside product)
    siteId: siteContext?.id ?? asset.site?.id,
    siteName: siteContext?.name ?? asset.site?.name,
    // Legacy shape often includes a nested site object
    site:
      siteContext?.id || asset.site
        ? {
            id: siteContext?.id ?? asset.site?.id,
            name: siteContext?.name ?? asset.site?.name,
          }
        : undefined,
  }

  // Map optional fields when present
  if (asset.adminEmail) {
    license.setupAdminEmail = asset.adminEmail
  }

  // Fields that do not exist in v4 but exist in legacy: log + omit
  if (asset.nsLicenseId === undefined) {
    wc.add('nsLicenseId')
  }
  if (asset.subscription === undefined) {
    wc.add('subscription')
  }

  if (!warnCollector) wc.flush()
  return license
}

export const adaptNetSuiteAssetsToLegacyLicenses = (
  assets,
  siteContext,
  warnCollector,
  derivedProgramCodes,
) => {
  if (!Array.isArray(assets)) return []
  const wc =
    warnCollector || createWarnCollector('adaptNetSuiteAssetsToLegacyLicenses')
  const result = assets
    .map(a =>
      adaptNetSuiteAssetToLegacyLicense(
        a,
        siteContext,
        wc,
        derivedProgramCodes,
      ),
    )
    .filter(Boolean)
  if (!warnCollector) wc.flush()
  return result
}

// Convert capabilities array (only scope === 'platform') to list of programCodes (names)
// Falls back to provided legacyCodes if capabilities empty or missing; caller handles warnings.
const capabilitiesToProgramCodes = (capabilities, legacyCodes) => {
  // Only include capabilities with scope === 'platform'. If any are present,
  // they fully replace legacy programCodes (do not merge). Fallback to
  // legacyCodes when capabilities missing/empty. Unknown capability fields
  // are ignored; we only read .name and .scope.
  if (!Array.isArray(capabilities) || !capabilities.length) {
    return legacyCodes || []
  }
  const codes = capabilities
    .filter(
      c =>
        c &&
        c.scope === 'platform' &&
        typeof c.name === 'string' &&
        c.name.length,
    )
    .map(c => c.name)
  return codes.length ? codes : legacyCodes || []
}

/**
 * Maps nodeFriendlyName from v4 API to nodeKey for highSchool pacing dates
 * @param {string} nodeFriendlyName - The friendly name from the API (e.g., "hs b&c collection 1")
 * @returns {string|null} - The corresponding nodeKey or null if not found
 */
const mapFriendlyNameToNodeKey = nodeFriendlyName => {
  if (!nodeFriendlyName) return null

  const mapping = {
    'hs program training': pacingNodesData.highSchool.nodeTraining,
    'hs b&c collection 1': pacingNodesData.highSchool.node1,
    'hs b&c collection 2': pacingNodesData.highSchool.node2,
    'hs b&c collection 3': pacingNodesData.highSchool.node3,
    'hs c&c collection 1': pacingNodesData.highSchool.node4,
    'hs c&c collection 2': pacingNodesData.highSchool.node5,
    'hs c&c collection 3': pacingNodesData.highSchool.node6,
    'hs a&o collection 1': pacingNodesData.highSchool.node7,
    'hs a&o collection 2': pacingNodesData.highSchool.node8,
    'hs a&o collection 3': pacingNodesData.highSchool.node9,
    'hs w&c collection 1': pacingNodesData.highSchool.node10,
    'hs w&c collection 2': pacingNodesData.highSchool.node11,
    'hs w&c collection 3': pacingNodesData.highSchool.node12,
  }

  return mapping[nodeFriendlyName] || null
}

/**
 * Transforms v4 API pacing dates to match expected format
 * Converts nodeFriendlyName to nodeKey and ensures proper structure
 * @param {Object} pacing - Pacing object from v4 API (e.g., { highSchool: [...], ... })
 * @returns {Object} - Transformed pacing object
 */
const transformV4Pacing = pacing => {
  if (!pacing || typeof pacing !== 'object') return pacing

  const transformed = {}
  for (const [programName, pacingDates] of Object.entries(pacing)) {
    if (!Array.isArray(pacingDates)) {
      transformed[programName] = pacingDates
      continue
    }

    // Only transform highSchool pacing dates
    if (programName !== 'highSchool' && programName !== 'highschool') {
      transformed[programName] = pacingDates
      continue
    }

    transformed[programName] = pacingDates.map(pacingDate => {
      // If already has nodeKey, return as-is (already transformed or v3 format)
      if (pacingDate.nodeKey) {
        return pacingDate
      }

      // Transform v4 format (with nodeFriendlyName) to expected format
      const nodeKey = mapFriendlyNameToNodeKey(pacingDate.nodeFriendlyName)
      const transformedDate = {
        ...pacingDate,
        nodeKey,
      }

      // Set nodeLevel for training node
      if (pacingDate.nodeFriendlyName === 'hs program training') {
        transformedDate.nodeLevel = 'training'
      }

      return transformedDate
    })
  }

  return transformed
}

export const adaptSitesV4ToLegacySites = v4Sites => {
  if (!Array.isArray(v4Sites)) return []

  const wc = createWarnCollector('adaptSitesV4ToLegacySites')
  const adapted = v4Sites.map(site => {
    // Normalize claims to legacy case-sensitive values expected by filters
    const normalizedClaims = Array.isArray(site.claims)
      ? site.claims.filter(Boolean).map(c => {
          if (typeof c !== 'string') return c
          const lc = c.toLowerCase()
          if (lc === 'teacher') return 'Teacher'
          if (lc === 'admin') return 'Admin'
          return c
        })
      : []

    // Derive programCodes from capabilities (platform scope); if capabilities non-empty they replace legacy programCodes.
    const originalProgramCodes = site.programCodes || []
    const derivedProgramCodes = capabilitiesToProgramCodes(
      site.capabilities,
      originalProgramCodes,
    )
    if (
      (!Array.isArray(site.capabilities) || !site.capabilities.length) &&
      Array.isArray(originalProgramCodes) &&
      originalProgramCodes.length
    ) {
      wc.add('capabilities missing -> fallback to legacy programCodes')
    }

    const licenses = adaptNetSuiteAssetsToLegacyLicenses(
      site.netSuiteAssets || [],
      { id: site.id, name: site.name },
      wc,
      derivedProgramCodes,
    )

    // Combine licenses from netSuiteAssets and site.licenses, then deduplicate by id
    const allLicenses = [...(licenses || []), ...(site.licenses || [])]
    const uniqueLicensesMap = new Map()
    const licensesWithoutId = []

    for (const license of allLicenses) {
      if (!license) continue

      if (license.id != null) {
        // Deduplicate licenses with id
        const licenseId = String(license.id)
        // Keep the first occurrence of each license id
        if (!uniqueLicensesMap.has(licenseId)) {
          uniqueLicensesMap.set(licenseId, license)
        }
      } else {
        // Licenses without id are included but not deduplicated (e.g., during fetch)
        licensesWithoutId.push(license)
      }
    }

    const deduplicatedLicenses = [
      ...Array.from(uniqueLicensesMap.values()),
      ...licensesWithoutId,
    ]

    const legacySite = {
      id: site.id,
      name: site.name,
      inactive: !!site.inactive,
      isRostered: !!site.isRostered,
      claims: normalizedClaims,
      // Legacy expects nested license list on site
      licenses: deduplicatedLicenses,
      // Best-effort mapping for customer id
      netsuiteCustomerId: site.netSuiteCustomer?.id,
      netSuiteCustomer: site.netSuiteCustomer,
      // Pass-through of address if present
      address: site.address,
      // Transform pacing dates from v4 format (nodeFriendlyName) to expected format (nodeKey)
      pacing: transformV4Pacing(site.pacing),
      // Convenience exposure of programCodes derived from capabilities
      programCodes: derivedProgramCodes,
      // Preserve client-managed field not returned by v4 API
      // This is set locally when updating pacing dates and must be surfaced to consumers
      schoolYearEndDate: site.schoolYearEndDate,
      // pass through site.preferences
      preferences: site.preferences,
    }

    // Convert v4 preferences[] into legacy sitePreferences shape
    const convertedPrefs = convertPreferencesArrayToLegacySitePreferences(
      site.preferences,
    )
    if (Object.keys(convertedPrefs).length) {
      legacySite.sitePreferences = convertedPrefs
    } else if (site.sitePreferences !== undefined) {
      // Fallback if v4 already provides sitePreferences in legacy shape
      legacySite.sitePreferences = site.sitePreferences
    } else if (site.preferences !== undefined) {
      wc.add('site.sitePreferences (generated from preferences[])')
    }

    // Handle HS per-site flags that should be promoted to sitePreferences.highSchool
    // These are site-level properties from v4 API that need to be mapped to preferences
    const hsFlags = [
      'isHSSetUp',
      'isHsImplementationPlanComplete',
      'isPacingComplete',
      'areUsersAdded',
    ]
    const hsPrefMap = {
      isHSSetUp: 'isSetup',
      isHsImplementationPlanComplete: 'isImplementationPlanComplete',
      isPacingComplete: 'isPacingComplete',
      areUsersAdded: 'areUsersAdded',
    }

    // Only process if site has any HS-related flags and we have HS program codes
    const hasHsProgramCodes =
      Array.isArray(derivedProgramCodes) &&
      derivedProgramCodes.some(code => ['HSP', 'K12P', 'HSPF'].includes(code))

    if (hasHsProgramCodes) {
      hsFlags.forEach(flag => {
        if (site[flag] === undefined) return

        // Preserve flag as a direct property (legacy v3 shape expected by consumers)
        legacySite[flag] = !!site[flag]

        // Ensure sitePreferences.highSchool exists
        if (!legacySite.sitePreferences) {
          legacySite.sitePreferences = {}
        }
        if (!legacySite.sitePreferences.highSchool) {
          legacySite.sitePreferences.highSchool = {}
        }

        // Map the flag to the preference key
        const prefKey = hsPrefMap[flag]
        if (prefKey) {
          legacySite.sitePreferences.highSchool[prefKey] = !!site[flag]
        }
      })
    }

    return legacySite
  })
  wc.flush()
  return adapted
}

// Build the minimal expired licenses view directly from v4 sites without
// converting to legacy site shapes. Expiration is derived using
// resolveIsExpired({ isActive, status, expirationDate, expirationDateOnly: true }).
// Program codes are derived from site.capabilities (platform scope) with fallback to legacy
// site.programCodes.
export const adaptSitesV4ToExpiredLicensesView = v4Sites => {
  if (!Array.isArray(v4Sites)) return []

  const result = []

  for (const site of v4Sites) {
    if (!site) continue

    // Normalize claims similar to legacy adapter
    const claims = Array.isArray(site.claims)
      ? site.claims.filter(Boolean).map(c => {
          if (typeof c !== 'string') return c
          const lc = c.toLowerCase()
          if (lc === 'teacher') return 'Teacher'
          if (lc === 'admin') return 'Admin'
          return c
        })
      : []

    // Derive program codes from capabilities with fallback
    const derivedProgramCodes = capabilitiesToProgramCodes(
      site.capabilities,
      site.programCodes || [],
    )

    const assets = Array.isArray(site.netSuiteAssets) ? site.netSuiteAssets : []

    for (const asset of assets) {
      if (!asset) continue
      if (asset.cancelledDate) continue
      const isExpired = resolveIsExpired({
        isActive: asset.isActive,
        status: asset.status,
        expirationDate: asset.expirationDate,
        expirationDateOnly: true,
      })
      if (!isExpired) continue

      // Build product payload using derived program codes (fallback to asset)
      const programCodes =
        derivedProgramCodes && derivedProgramCodes.length
          ? derivedProgramCodes
          : asset.product?.programCodes || []
      if (!Array.isArray(programCodes) || programCodes.length === 0) continue

      result.push({
        id: asset.id,
        expirationDate: asset.expirationDate || null,
        siteName: site.name,
        product: {
          programCodes: asset.product?.programCodes || derivedProgramCodes,
          // Legacy logic expects a category on product.type; use BULK as in legacy adapter
          type: 'BULK',
          skuCode: asset.product?.skuValue,
        },
        claims,
      })
    }
  }

  return result
}

export default {
  adaptSitesV4ToLegacySites,
  adaptNetSuiteAssetsToLegacyLicenses,
  adaptNetSuiteAssetToLegacyLicense,
  adaptSitesV4ToExpiredLicensesView,
}
